import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { PaperPlaneTilt, CircleNotch, Envelope } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SendEmailModal({ open, onClose, docType, docId, docNumber, supplierEmail, supplierName, companyName }) {
  const { token } = useAuth();
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ to_email: '', subject: '', message: '' });

  // Reset form when modal opens with new document
  useEffect(() => {
    if (open) {
      setForm({ to_email: supplierEmail || '', subject: '', message: '' });
      setSending(false);
    }
  }, [open, supplierEmail]);

  const docLabels = { orders: 'Purchase Order', rfq: 'Request for Quotation', grn: 'Goods Receipt Note' };
  const defaultSubject = `${docLabels[docType] || 'Document'} - ${docNumber} from ${companyName || 'Our Company'}`;
  const defaultMessage = `Dear ${supplierName || 'Sir/Madam'},\n\nPlease find attached ${docLabels[docType] || 'document'} ${docNumber}.\n\nKindly review and confirm.\n\nRegards,\n${companyName || 'Team'}`;

  const handleSend = async () => {
    if (!form.to_email) {
      toast.error('Recipient email is required');
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/email/send-document`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          doc_type: docType,
          doc_id: docId,
          to_email: form.to_email,
          subject: form.subject || defaultSubject,
          message: form.message || defaultMessage
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to send email');
      toast.success(data.message);
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="send-email-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Envelope size={20} className="text-emerald-600" />
            Send {docLabels[docType] || 'Document'} via Email
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="p-3 bg-muted rounded-lg">
            <p className="text-sm font-medium">{docNumber}</p>
            <p className="text-xs text-muted-foreground">PDF will be generated and attached automatically</p>
          </div>

          <div className="space-y-2">
            <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Recipient Email</Label>
            <Input
              type="email"
              placeholder="supplier@company.com"
              value={form.to_email}
              onChange={(e) => setForm(f => ({ ...f, to_email: e.target.value }))}
              data-testid="send-email-to"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Subject</Label>
            <Input
              placeholder={defaultSubject}
              value={form.subject}
              onChange={(e) => setForm(f => ({ ...f, subject: e.target.value }))}
              data-testid="send-email-subject"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Message</Label>
            <Textarea
              rows={5}
              placeholder={defaultMessage}
              value={form.message}
              onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
              className="resize-none"
              data-testid="send-email-message"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending} data-testid="cancel-send-email">
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-send-email">
            {sending ? <CircleNotch size={16} className="mr-2 animate-spin" /> : <PaperPlaneTilt size={16} className="mr-2" />}
            {sending ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
